import { Injectable } from '@angular/core'

import { Observable, catchError, map, of, throwError } from 'rxjs'

import {
  AdminOrg,
  PeriodKeyActionScore,
  RecentKeyActionScore,
  User,
  UserPhoneNumber,
  mappingUsers,
  mappingUsersForAdmin,
} from './user.mapping'
import { assertGraphqlData } from '../../util/assertGraphqlData'
import {
  AssignLicenseGQL,
  CreateUserGQL,
  GetAdminOrgGQL,
  GetRolesGQL,
  GetUserByIdGQL,
  GetUserOneSelfGQL,
  RevokeLicenseGQL,
  SearchUsersForAdminGQL,
  SearchUsersGQL,
  UpdateUserGQL,
  LicensePlan,
  DeleteUsersGQL,
  SendInvitationsGQL,
  AssessmentStatus,
  GetDepartmentNamesGQL,
  PrivacySetting,
  DefaultSkillMapTarget,
  UpdatePrivacySettingGQL,
  GetWeeklyUserScoresGQL,
  GetWeeklyScoresGQL,
  BulkCreateUserGQL,
  BulkCreateUserParam,
  GetMfaStatusGQL,
  GetMfaStatusQuery,
  UpdateRequiredMfaGQL,
  ResetMfaGQL,
  AddUserPhoneNumberGQL,
  DeleteUserPhoneNumberGQL,
  GetAllowedIpAddressesGQL,
  SaveAllowedIpAddressesGQL,
  SaveUseRoleModelRecommendationGQL,
  UpdateDefaultSkillMapGQL,
  ResetPasswordGQL,
  ReportToOptionsGQL,
  ReportToOption,
} from '../../../_generated/graphql'
import {
  throwIfCustomError,
  CustomErrorCode,
} from '../../util/throwIfCustomError'

@Injectable({
  providedIn: 'root',
})
export class UserService {
  constructor(
    private getUserOneSelfGQL: GetUserOneSelfGQL,
    private assignLicenseGQL: AssignLicenseGQL,
    private revokeLicenseGQL: RevokeLicenseGQL,
    private searchUsersGQL: SearchUsersGQL,
    private getUserByIdGQL: GetUserByIdGQL,
    private searchUsersForAdminGQL: SearchUsersForAdminGQL,
    private getAdminOrgGQL: GetAdminOrgGQL,
    private getAllowedIPAddressesGQL: GetAllowedIpAddressesGQL,
    private getRolesGQL: GetRolesGQL,
    private updateUserGQL: UpdateUserGQL,
    private createUserGQL: CreateUserGQL,
    private bulkCreateUserGQL: BulkCreateUserGQL,
    private deleteUsersGQL: DeleteUsersGQL,
    private sendInvitationsGQL: SendInvitationsGQL,
    private getDepartmentNamesGQL: GetDepartmentNamesGQL,
    private updatePrivacySettingGQL: UpdatePrivacySettingGQL,
    private getWeeklyUserScoresGQL: GetWeeklyUserScoresGQL,
    private getWeeklyScoresGQL: GetWeeklyScoresGQL,
    private getMfaStatusGQL: GetMfaStatusGQL,
    private updateRequiredMfaGQL: UpdateRequiredMfaGQL,
    private resetMfaGQL: ResetMfaGQL,
    private addUserPhoneNumberGQL: AddUserPhoneNumberGQL,
    private deleteUserPhoneNumberGQL: DeleteUserPhoneNumberGQL,
    private saveAllowedIPAddressesGQL: SaveAllowedIpAddressesGQL,
    private saveUseRoleModelRecommendationGQL: SaveUseRoleModelRecommendationGQL,
    private updateDefaultSkillMapGQL: UpdateDefaultSkillMapGQL,
    private resetPasswordGQL: ResetPasswordGQL,
    private reportToOptionsGQL: ReportToOptionsGQL,
  ) {}

  /** 閲覧範囲内の上司候補を取得する。 */
  getReportToOptions(): Observable<ReportToOption[]> {
    return this.reportToOptionsGQL.fetch({ fetchPolicy: 'no-cache' }).pipe(
      map((res) => {
        assertGraphqlData(res)
        return res.data.reportToOptions
      }),
      catchError((error: unknown) => {
        throwIfCustomError(error)
        return throwError(() => error)
      }),
    )
  }

  getOneSelf(useCache: boolean): Observable<UserInfo> {
    return this.getUserOneSelfGQL
      .fetch({
        fetchPolicy: useCache ? 'cache-first' : 'no-cache',
      })
      .pipe(
        map((res) => {
          assertGraphqlData(res)
          if (!res.data.getUserOneSelf) {
            throw new Error('User not found')
          }
          return mappingUserInfo(res.data.getUserOneSelf)
        }),
        catchError((error: unknown) => {
          throwIfCustomError(error)
          return of()
        }),
      )
  }
  getUser(userId: string): Observable<User> {
    return this.getUserByIdGQL
      .fetch({
        variables: { userId: userId },
        fetchPolicy: 'no-cache',
      })
      .pipe(
        map((res) => {
          const user = res.data?.getUserById
          if (!user) {
            alert('User not found')
            throw new Error('User not found')
          }
          return mappingUsers([user])[0]
        }),
      )
  }

  searchUsers(
    query: string,
    departmentNames: string[],
    paginatin?: { limit: number; offset: number },
    sort?: { sortField: string; sortOrder: 'asc' | 'desc' },
    useCache = true,
    yearMonth?: string,
  ) {
    return this.searchUsersGQL
      .fetch({
        variables: {
          query: query,
          departmentNames: departmentNames,
          pagination: paginatin,
          sort: sort,
          yearMonth: yearMonth,
        },
        fetchPolicy: useCache ? 'cache-first' : 'no-cache',
      })
      .pipe(
        map((res) => {
          return mappingUsers(res.data?.getUsers || [])
        }),
      )
  }

  searchUsersForAdmin(
    query: string,
    departmentNames: string[],
    paginatin?: { limit: number; offset: number },
    sort?: { sortField: string; sortOrder: 'asc' | 'desc' },
  ) {
    return this.searchUsersForAdminGQL
      .fetch({
        variables: {
          query: query,
          departmentNames: departmentNames,
          pagination: paginatin,
          sort: sort,
        },
        fetchPolicy: 'no-cache',
      })
      .pipe(
        map((res) => {
          return mappingUsersForAdmin(res.data?.getUsers || [])
        }),
      )
  }

  getAdminOrg(): Observable<AdminOrg> {
    return this.getAdminOrgGQL
      .fetch({
        fetchPolicy: 'no-cache',
      })
      .pipe(
        map((res) => {
          assertGraphqlData(res)
          return {
            licenseInfo: res.data.getAdminOrg.licenseInfo ?? null,
            departmentNames: res.data.getAdminOrg.departmentNames,
            privacySetting: res.data.getAdminOrg.privacySetting,
            requiredMfa: res.data.getAdminOrg.requiredMfa,
            useRoleModelRecommendation:
              res.data.getAdminOrg.useRoleModelRecommendation,
            scoreImprovementThreshold:
              res.data.getAdminOrg.scoreImprovementThreshold,
          }
        }),
      )
  }

  getAllowedIPAddresses(): Observable<string[]> {
    return this.getAllowedIPAddressesGQL
      .fetch({
        fetchPolicy: 'no-cache',
      })
      .pipe(
        map((res) => {
          assertGraphqlData(res)
          return res.data.getAdminOrg.allowedIPAddresses
        }),
      )
  }

  getRoles(): Observable<{ name: string; code: string }[]> {
    return this.getRolesGQL.fetch().pipe(
      map((res) => {
        assertGraphqlData(res)
        return res.data.getRoles.map((role) => ({
          name: role.name,
          code: role.code,
        }))
      }),
    )
  }

  assignLicense(userId: string, licenseId: string) {
    return this.assignLicenseGQL.mutate({
      variables: {
        userId: userId,
        licenseId: licenseId,
      },
    })
  }

  revokeLicense(userId: string) {
    return this.revokeLicenseGQL.mutate({
      variables: {
        userId: userId,
      },
    })
  }

  updateUser(userInput: {
    id: string
    name: string
    email: string
    departmentName: string
    reportToEmail: string | null
    roleCodes: string[]
    licenseId: string | null
    requiredMfa: boolean
  }) {
    return this.updateUserGQL
      .mutate({
        variables: {
          param: {
            id: userInput.id,
            name: userInput.name,
            email: userInput.email,
            departmentName: userInput.departmentName,
            reportToEmail: userInput.reportToEmail,
            roles: userInput.roleCodes,
            licenseId: userInput.licenseId,
            requiredMfa: userInput.requiredMfa,
          },
        },
      })
      .pipe(
        catchError((error: unknown) => {
          throwIfCustomError(error)
          return of()
        }),
      )
  }

  deleteUsers(userIds: string[]) {
    return this.deleteUsersGQL
      .mutate({
        variables: {
          userIds: userIds,
        },
      })
      .pipe(
        catchError((error: unknown) => {
          throwIfCustomError(error)
          return of()
        }),
      )
  }

  addUserPhoneNumber(
    userId: string,
    phoneNumber: string,
  ): Observable<UserPhoneNumber> {
    return this.addUserPhoneNumberGQL
      .mutate({
        variables: {
          userId,
          phoneNumber,
        },
      })
      .pipe(
        map((res) => {
          const userPhoneNumber = res.data?.addUserPhoneNumber
          if (!userPhoneNumber) {
            throw new Error('User phone number not found')
          }
          return {
            id: userPhoneNumber.id,
            phoneNumber: userPhoneNumber.phoneNumber,
            createdAt: userPhoneNumber.createdAt,
          }
        }),
        catchError((error: unknown) => {
          throwIfCustomError(error)
          return throwError(() => error)
        }),
      )
  }

  deleteUserPhoneNumber(id: string): Observable<void> {
    return this.deleteUserPhoneNumberGQL.mutate({ variables: { id } }).pipe(
      map(() => {}),
      catchError((error: unknown) => {
        throwIfCustomError(error)
        return throwError(() => error)
      }),
    )
  }

  sendInvitations(userIds: string[]) {
    return this.sendInvitationsGQL
      .mutate({
        variables: {
          userIds: userIds,
        },
      })
      .pipe(
        catchError((error: unknown) => {
          throwIfCustomError(error)
          return of()
        }),
      )
  }

  resetPassword(email: string) {
    return this.resetPasswordGQL
      .mutate({
        variables: {
          email: email,
        },
      })
      .pipe(map((res) => res.data?.resetPassword))
  }

  saveAllowedIPAddresses(allowedIPAddresses: string[]) {
    return this.saveAllowedIPAddressesGQL
      .mutate({
        variables: {
          allowedIPAddresses: allowedIPAddresses,
        },
      })
      .pipe(
        catchError((error: unknown) => {
          throwIfCustomError(error)
          return of()
        }),
      )
  }

  updateDefaultSkillMap(
    target: DefaultSkillMapTarget,
    skillMapId: string | null,
  ) {
    return this.updateDefaultSkillMapGQL
      .mutate({ variables: { target, skillMapId } })
      .pipe(
        map((res) => {
          assertGraphqlData(res)
          return res.data.updateDefaultSkillMap
        }),
        catchError((error: unknown) => {
          throwIfCustomError(error)
          return throwError(() => error)
        }),
      )
  }

  saveUseRoleModelRecommendation(
    useRoleModelRecommendation: boolean,
  ): Observable<void> {
    return this.saveUseRoleModelRecommendationGQL
      .mutate({
        variables: {
          useRoleModelRecommendation,
        },
      })
      .pipe(map(() => {}))
  }

  createUser(userInput: {
    name: string
    email: string
    departmentName: string
    reportToEmail: string | null
    roleCodes: string[]
    licenseId: string
    requiredMfa: boolean
  }) {
    return this.createUserGQL
      .mutate({
        variables: {
          param: {
            name: userInput.name,
            email: userInput.email,
            departmentName: userInput.departmentName,
            reportToEmail: userInput.reportToEmail,
            roles: userInput.roleCodes,
            licenseId: userInput.licenseId,
            requiredMfa: userInput.requiredMfa,
          },
        },
      })
      .pipe(
        catchError((error: unknown) => {
          throwIfCustomError(error)
          return of()
        }),
      )
  }

  bulkCreateUser(params: BulkCreateUserParam[]) {
    return this.bulkCreateUserGQL.mutate({ variables: { params } }).pipe(
      catchError((error: unknown) => {
        throwIfCustomError(error)
        return of()
      }),
    )
  }

  getDepartmentNames(): Observable<string[]> {
    return this.getDepartmentNamesGQL
      .fetch({
        fetchPolicy: 'no-cache',
      })
      .pipe(
        map((res) => {
          assertGraphqlData(res)
          return res.data.getAdminOrg.departmentNames
        }),
      )
  }

  getWeeklyUserScores(
    userId: string,
    startDate: Date,
    endDate: Date,
    skillMapId: string,
  ): Observable<{
    id: string
    name: string
    departmentName: string
    thumbnailPath: string | null
    keyActionRecentlyAverageScores: RecentKeyActionScore[]
    periodKeyActionScores: PeriodKeyActionScore[]
  }> {
    return this.getWeeklyUserScoresGQL
      .fetch({
        variables: {
          userId: userId,
          startDate: startDate,
          endDate: endDate,
          skillMapIds: [skillMapId],
        },
      })
      .pipe(
        map((res) => {
          assertGraphqlData(res)
          const user = res.data.getUserById
          if (!user) {
            throw new Error(`we can't get user by id: ${userId}`)
          }
          return {
            id: user.id,
            name: user.name,
            departmentName: user.departmentName ?? '',
            thumbnailPath: user.thumbnailPath ?? null,
            keyActionRecentlyAverageScores:
              user.keyActionRecentlyAverageScores.map((score) => ({
                keyAction: {
                  id: score.keyAction.id,
                  name: score.keyAction.name,
                },
                score: score.recentlyAverageScore ?? null,
              })),
            periodKeyActionScores: user.periodKeyActionScores.map((score) => ({
              keyAction: {
                id: score.keyAction.id,
                name: score.keyAction.name,
              },
              weeklyScores: score.weeklyScores.map((weeklyScore) => ({
                weekStartDate: new Date(weeklyScore.weekStartDate),
                score: weeklyScore.score ?? null,
              })),
            })),
          }
        }),
      )
  }

  getWeeklyScores(
    skillMapId: string,
    startDate: Date,
    endDate: Date,
  ): Observable<{
    name: string
    periodKeyActionScores: PeriodKeyActionScore[]
    departments: {
      name: string
      periodKeyActionScores: PeriodKeyActionScore[]
    }[]
    users: {
      departmentName: string
      recentKeyActionScores: RecentKeyActionScore[]
    }[]
  }> {
    return this.getWeeklyScoresGQL
      .fetch({
        variables: {
          skillMapIds: [skillMapId],
          startDate: startDate,
          endDate: endDate,
        },
      })
      .pipe(
        map((res) => {
          assertGraphqlData(res)
          return {
            name: res.data.getAdminOrg.name,
            periodKeyActionScores:
              res.data.getAdminOrg.periodKeyActionScores.map((score) => ({
                keyAction: {
                  id: score.keyAction.id,
                  name: score.keyAction.name,
                },
                weeklyScores: score.weeklyScores.map((weeklyScore) => ({
                  weekStartDate: new Date(weeklyScore.weekStartDate),
                  score: weeklyScore.score ?? null,
                })),
              })),
            departments: res.data.getAdminOrg.departments.map((department) => ({
              name: department.name,
              periodKeyActionScores: department.periodKeyActionScores.map(
                (score) => ({
                  keyAction: {
                    id: score.keyAction.id,
                    name: score.keyAction.name,
                  },
                  weeklyScores: score.weeklyScores.map((weeklyScore) => ({
                    weekStartDate: new Date(weeklyScore.weekStartDate),
                    score: weeklyScore.score ?? null,
                  })),
                }),
              ),
            })),
            users: res.data.getAdminOrg.users.map((user) => ({
              departmentName:
                !user.departmentName || user.departmentName === 'noDepartment'
                  ? '所属無し'
                  : user.departmentName,
              recentKeyActionScores: user.keyActionRecentlyAverageScores.map(
                (score) => ({
                  keyAction: {
                    id: score.keyAction.id,
                    name: score.keyAction.name,
                  },
                  score: score.recentlyAverageScore ?? null,
                }),
              ),
            })),
          }
        }),
      )
  }

  updatePrivacySetting(setting: PrivacySettingType): Observable<void> {
    return this.updatePrivacySettingGQL
      .mutate({ variables: { privacySetting: setting as PrivacySetting } })
      .pipe(map(() => {}))
  }

  updateRequiredMfa(requiredMfa: boolean): Observable<void> {
    return this.updateRequiredMfaGQL
      .mutate({ variables: { requiredMfa } })
      .pipe(map(() => {}))
  }

  resetMfa(userId: string): Observable<void> {
    return this.resetMfaGQL
      .mutate({
        variables: {
          userId,
        },
      })
      .pipe(
        map(() => {}),
        catchError((error: unknown) => {
          throwIfCustomError(error, [CustomErrorCode.MFA_RESET_INCOMPLETE])
          return throwError(() => error)
        }),
      )
  }

  getMfaStatus(): Observable<MfaStatus> {
    return this.getMfaStatusGQL.fetch().pipe(
      map((res) => {
        assertGraphqlData(res)
        return res.data.getMfaStatus
      }),
      catchError((error) => {
        throwIfCustomError(error)
        return throwError(() => error)
      }),
    )
  }
}

const mappingUserInfo = (data: FetchUserInfo): UserInfo => {
  return {
    id: data.id,
    orgId: data.orgId,
    name: data.name,
    email: data.email,
    departmentName: data.departmentName ?? null,
    roles: data.roles ?? [],
    isManager: !!data.isManager,
    thumbnailPath: data.thumbnailPath ?? null,
    license: data.license ?? null,
    enabledFeatures: data.enabledFeatures ?? [],
  }
}

export interface FetchUserInfo {
  id: string
  orgId: string
  name: string
  email: string
  departmentName?: string | null
  roles?: { name: string; code: string }[]
  isManager?: boolean
  thumbnailPath?: string | null
  license?: {
    plan: LicensePlan
  } | null
  enabledFeatures?: string[]
}

export interface UserLicense {
  plan: LicensePlan
  userMonthlyLimitSeconds: number
  extraChargeLimitSeconds: number
  usedSeconds: number
}

export interface UserInfo {
  id: string
  orgId: string
  name: string
  email: string
  departmentName: string | null
  roles: { name: string; code: string }[]
  isManager: boolean
  thumbnailPath: string | null
  license: {
    plan: LicensePlan
  } | null
  enabledFeatures: string[]
}

export interface FetchedUser {
  id: string
  orgId: string
  name: string
  email: string
  departmentName?: string | null
  thumbnailPath?: string | null
  roleplayCount?: number
  assessments?: {
    id: string
    status: AssessmentStatus
    assessDate: Date
    businessMeeting: { title: string; recordDate: Date }
  }[]
  latestAssessment?: {
    id: string
    assessDate: Date
    businessMeeting: { title: string; recordDate: Date }
    keyActionEvaluations: { score: number }[]
  } | null
  license?: UserLicense | null
  reportTo?: {
    id: string
  } | null
}

export type FetchedUserForAdmin = Omit<
  FetchedUser,
  'assessments' | 'license'
> & {
  requiredMfa: boolean
  reportTo?: { id: string; name: string; email: string } | null
  roles: { name: string; code: string }[]
  phoneNumbers: UserPhoneNumber[]
  license?: {
    id: string
    plan: 'BASIC' | 'ADVANCED'
    userMonthlyLimitSeconds: number
    extraChargeLimitSeconds: number
    usedSeconds: number
  } | null
}

export const EMAIL_REGEXP =
  /^(?=.{1,254}$)(?=.{1,64}@)[a-z0-9!#$%&'*+/=?^_`{|}~-]+(?:\.[a-z0-9!#$%&'*+/=?^_`{|}~-]+)*@[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?(?:\.[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?)*$/

export type PrivacySettingType =
  | 'FULLY_OPEN'
  | 'PARTIALLY_OPEN'
  | 'FULLY_CLOSED'

export type MfaStatus = GetMfaStatusQuery['getMfaStatus']
